import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  INSTALL_DISMISSED_KEY,
  isIosSafari,
  isStandalone,
  shouldShowInstallHint,
} from '../lib/pwaInstall';

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed'; platform: string }>;
}

export default function InstallAppBanner(): React.ReactElement | null {
  const { t } = useTranslation('misc');
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null);
  const [isDismissed, setIsDismissed] = useState<boolean>(false);
  const promptRef = useRef<BeforeInstallPromptEvent | null>(null);

  useEffect(() => {
    promptRef.current = deferredPrompt;
  }, [deferredPrompt]);

  useEffect(() => {
    const handleBeforeInstallPrompt = (event: Event) => {
      event.preventDefault();
      const promptEvent = event as BeforeInstallPromptEvent;
      promptRef.current = promptEvent;
      setDeferredPrompt(promptEvent);
    };

    const handleAppInstalled = () => {
      promptRef.current = null;
      setDeferredPrompt(null);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    window.addEventListener('appinstalled', handleAppInstalled);

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
      window.removeEventListener('appinstalled', handleAppInstalled);
    };
  }, []);

  if (isDismissed) {
    return null;
  }

  let dismissedAt: number | null = null;
  try {
    const stored = window.localStorage.getItem(INSTALL_DISMISSED_KEY);
    if (stored) {
      const parsed = parseInt(stored, 10);
      if (!Number.isNaN(parsed)) {
        dismissedAt = parsed;
      }
    }
  } catch {
    // Безопасный фоллбэк при недоступности localStorage
  }

  const nav = window.navigator as Navigator & { standalone?: boolean };
  const standalone = isStandalone((q) => window.matchMedia(q), nav.standalone);
  const ios = isIosSafari(window.navigator.userAgent, window.navigator.maxTouchPoints || 0);

  const hintType = shouldShowInstallHint({
    standalone,
    hasPrompt: Boolean(deferredPrompt),
    ios,
    dismissedAt,
    now: Date.now(),
  });

  if (!hintType) {
    return null;
  }

  const handleInstallClick = async () => {
    const currentPrompt = promptRef.current || deferredPrompt;
    if (!currentPrompt) {
      return;
    }
    try {
      await currentPrompt.prompt();
      await currentPrompt.userChoice;
    } finally {
      promptRef.current = null;
      setDeferredPrompt(null);
    }
  };

  const handleDismiss = () => {
    try {
      window.localStorage.setItem(INSTALL_DISMISSED_KEY, Date.now().toString());
    } catch {
      // Игнорируем ошибку квоты или приватного режима
    }
    setIsDismissed(true);
  };

  return (
    <aside className="install-banner">
      <div className="install-banner__text">
        <strong>{t('installApp.title')}</strong>
        {hintType === 'ios' && <span> {t('installApp.iosHint')}</span>}
      </div>
      {hintType === 'prompt' && (
        <button
          type="button"
          className="install-banner__btn"
          onClick={handleInstallClick}
        >
          {t('installApp.install')}
        </button>
      )}
      <button
        type="button"
        className="install-banner__btn"
        aria-label={t('installApp.dismiss')}
        onClick={handleDismiss}
      >
        ×
      </button>
    </aside>
  );
}
